import React, { useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { maskPhoneBR, localDigitsBR } from "@/lib/format";
import { Card, Field, TextArea, SaveBtn } from "./ui";
import Uploader from "../common/Uploader";

export default function StoreModule({ rid, data, reload }) {
  const [f, setF] = useState({
    ...data.restaurant,
    menu_theme: data.restaurant.menu_theme === "light" ? "light" : "dark",
    whatsapp: maskPhoneBR(data.restaurant.whatsapp),
    phone: maskPhoneBR(data.restaurant.phone),
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setAddr = (k, v) => setF((p) => ({ ...p, address: { ...p.address, [k]: v } }));

  const lookupCep = async () => {
    const cep = (f.address?.cep || "").replace(/\D/g, "");
    if (cep.length !== 8) return;
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const j = await res.json();
      if (j.erro) return toast.error("CEP não encontrado");
      setF((p) => ({ ...p, address: { ...p.address, street: j.logradouro || p.address.street, neighborhood: j.bairro || p.address.neighborhood, city: j.localidade || p.address.city, state: j.uf || p.address.state } }));
      toast.success("Endereço preenchido");
    } catch { toast.error("Erro ao buscar CEP"); }
  };

  const save = async () => {
    setLoading(true);
    try {
      const patch = (({ name, description, tagline, segment, logo, cover, cover_video, cover_type, whatsapp, instagram, phone, address, delivery_fee, min_order, avg_time, delivery_enabled, pickup_enabled, menu_theme }) =>
        ({ name, description, tagline, segment, logo, cover, cover_video, cover_type, whatsapp, instagram, phone, address, delivery_fee: Number(delivery_fee) || 0, min_order: Number(min_order) || 0, avg_time, delivery_enabled, pickup_enabled, menu_theme }))(f);
      patch.instagram = (patch.instagram || "").replace(/@/g, "").trim();
      patch.whatsapp = localDigitsBR(patch.whatsapp);
      patch.phone = localDigitsBR(patch.phone);
      await api.put(`/restaurants/${rid}`, patch);
      toast.success("Loja atualizada");
      reload();
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5" data-testid="store-module">
      <Card title="Tema do cardápio" desc="Escolha a aparência do seu cardápio público. Seus clientes não podem alterar.">
        <div className="grid grid-cols-2 gap-3">
          {[["dark", "Escuro", "Fundo escuro premium — padrão HMMM"], ["light", "Claro", "Fundo off-white quente e elegante"]].map(([val, label, hint]) => {
            const active = f.menu_theme === val;
            return (
              <button key={val} onClick={() => set("menu_theme", val)} data-testid={`store-theme-${val}`}
                className={`text-left rounded-2xl border p-4 transition-colors ${active ? "border-[#FF6B00] bg-[#FF6B00]/10" : "border-white/10 bg-white/[0.03] hover:border-white/20"}`}>
                <div className="flex items-center gap-2">
                  <span className={`h-8 w-8 rounded-lg border ${val === "dark" ? "bg-[#0B0B0D] border-white/10" : "bg-[#FBF6EF] border-black/10"}`} />
                  <span className="font-head font-bold text-sm">{label}</span>
                  {active && <span className="ml-auto text-[10px] font-bold hmmm-text">Ativo</span>}
                </div>
                <p className="text-xs text-zinc-500 mt-2">{hint}</p>
              </button>
            );
          })}
        </div>
      </Card>

      <Card title="Identidade">
        <div className="space-y-3">
          <Field label="Nome do restaurante" value={f.name} onChange={(v) => set("name", v)} tid="store-name" />
          <Field label="Segmento" value={f.segment} onChange={(v) => set("segment", v)} tid="store-segment" placeholder="Ex: Hamburgueria Artesanal" />
          <TextArea label="Descrição" value={f.description} onChange={(v) => set("description", v)} tid="store-desc" />
          <Field label="Tagline" value={f.tagline} onChange={(v) => set("tagline", v)} tid="store-tagline" placeholder="Frase curta e elegante" />
        </div>
      </Card>

      <Card title="Mídia" desc="Logo, capa em imagem ou vídeo (autoplay, mudo, em loop).">
        <div className="grid sm:grid-cols-2 gap-4">
          <Uploader value={f.logo} onChange={(v) => set("logo", v)} label="Logo" tid="store-logo" />
          <div>
            <label className="text-xs font-semibold text-zinc-400">Tipo de capa</label>
            <div className="mt-1.5 flex gap-2">
              {["image", "video"].map((t) => (
                <button key={t} onClick={() => set("cover_type", t)} data-testid={`store-covertype-${t}`}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-semibold ${f.cover_type === t ? "bg-[#FF6B00] text-white" : "bg-white/5 text-zinc-400"}`}>
                  {t === "image" ? "Imagem" : "Vídeo"}
                </button>
              ))}
            </div>
          </div>
          <Uploader value={f.cover} onChange={(v) => set("cover", v)} label="Capa (imagem / poster)" tid="store-cover" />
          {f.cover_type === "video" && <Uploader value={f.cover_video} onChange={(v) => set("cover_video", v)} onUploaded={(d) => { if (d?.poster && !f.cover) set("cover", d.poster); }} label="Vídeo da capa" kind="video" accept="video/*" tid="store-covervideo" />}
        </div>
      </Card>

      <Card title="Contato">
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="WhatsApp" value={f.whatsapp} onChange={(v) => set("whatsapp", maskPhoneBR(v))} tid="store-whatsapp" placeholder="(11) 99999-0000" />
          <Field label="Instagram (sem @)" value={f.instagram} onChange={(v) => set("instagram", v)} tid="store-instagram" />
          <Field label="Telefone" value={f.phone} onChange={(v) => set("phone", maskPhoneBR(v))} tid="store-phone" placeholder="(11) 3333-4444" />
        </div>
      </Card>

      <Card title="Endereço" desc="Use o CEP para preencher automaticamente.">
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="flex items-end gap-2">
            <Field label="CEP" value={f.address?.cep} onChange={(v) => setAddr("cep", v)} tid="store-cep" />
            <button onClick={lookupCep} data-testid="store-cep-btn" className="h-[42px] px-3 rounded-xl bg-white/10 text-sm font-semibold shrink-0">Buscar</button>
          </div>
          <Field label="Rua" value={f.address?.street} onChange={(v) => setAddr("street", v)} tid="store-street" />
          <Field label="Número" value={f.address?.number} onChange={(v) => setAddr("number", v)} tid="store-number" />
          <Field label="Bairro" value={f.address?.neighborhood} onChange={(v) => setAddr("neighborhood", v)} tid="store-neighborhood" />
          <Field label="Cidade" value={f.address?.city} onChange={(v) => setAddr("city", v)} tid="store-city" />
          <Field label="Estado" value={f.address?.state} onChange={(v) => setAddr("state", v)} tid="store-state" />
          <Field label="Complemento" value={f.address?.complement} onChange={(v) => setAddr("complement", v)} tid="store-complement" />
        </div>
      </Card>

      <Card title="Comercial">
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Taxa de entrega (R$)" type="number" value={f.delivery_fee} onChange={(v) => set("delivery_fee", v)} tid="store-fee" />
          <Field label="Pedido mínimo (R$)" type="number" value={f.min_order} onChange={(v) => set("min_order", v)} tid="store-min" />
          <Field label="Tempo médio" value={f.avg_time} onChange={(v) => set("avg_time", v)} tid="store-time" placeholder="30-45 min" />
        </div>
        <div className="flex gap-4 mt-4">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.delivery_enabled} onChange={(e) => set("delivery_enabled", e.target.checked)} data-testid="store-delivery" /> Entrega</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.pickup_enabled} onChange={(e) => set("pickup_enabled", e.target.checked)} data-testid="store-pickup" /> Retirada</label>
        </div>
      </Card>

      <div className="flex justify-end"><SaveBtn onClick={save} loading={loading} tid="store-save" /></div>
    </div>
  );
}
